import axios from 'axios';
import api from './api';
import { getCurrentLang } from '../common/lang';
import Vue from 'vue';
import { Base64 } from 'js-base64';
import { Toast } from 'vant';
import qs from 'qs';

Vue.use(Toast);

// Create axios instance with proxy friendly defaults
const instance = axios.create({
  baseURL: '/api',
  timeout: 5000,
  withCredentials: true,
});

instance.interceptors.request.use(
  (config) => {
    const headers = { ...config.headers };

    if (config.method === 'post' && !(config.data instanceof FormData)) {
      headers['content-type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    const token = localStorage.getItem('token');
    const currentLang = getCurrentLang();
    headers.lang = currentLang;

    if (token) {
      headers.token = Base64.encode(token);
    }

    return { ...config, headers };
  },
  (error) => {
    console.error('request interceptor failed', error);
    return Promise.reject(error);
  }
);

function decodePayload(payload) {
  if (typeof payload !== 'string') {
    return payload;
  }

  const trimmed = payload.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const decoded = Base64.decode(trimmed);
    try {
      return JSON.parse(decoded);
    } catch (parseError) {
      console.warn('response body is not JSON; returning decoded text');
      return decoded;
    }
  } catch (decodeError) {
    console.warn('base64 decode failed; returning original payload');
    return payload;
  }
}

function normalizeSuccessResponse(response) {
  const body = response?.data ? { ...response.data } : {};
  const hasDataField = Object.prototype.hasOwnProperty.call(body, 'data');

  if (hasDataField) {
    body.data = decodePayload(body.data);
  }

  const code = body.code;
  const numericCode = typeof code === 'undefined' ? undefined : Number(code);
  const message = body.msg || body.message || '';
  const ok =
    typeof numericCode === 'undefined'
      ? true
      : numericCode === 0 || numericCode === 200;

  return {
    ...body,
    ok,
    msg: message,
    message,
    raw: response,
    data: hasDataField ? body.data : body,
  };
}

function normalizeErrorResponse(error) {
  const response = error?.response;
  const body = response?.data ? { ...response.data } : {};
  const message = body.msg || body.message || error.message || 'network request failed';

  return {
    ok: false,
    code: body.code ?? response?.status ?? -1,
    msg: message,
    message,
    data: null,
    raw: error,
  };
}

async function http(option = {}) {
  const method = (option.method || 'get').toLowerCase();
  const requestData = option.data || {};
  const endpoint = api[option.url] || option.url;

  if (!endpoint) {
    const message = `missing endpoint: ${option.url}`;
    Toast.fail(message);
    return {
      ok: false,
      code: -1,
      msg: message,
      message,
      data: null,
      raw: null,
    };
  }

  try {
    let response;
    if (method === 'get' || method === 'delete') {
      response = await instance[method](endpoint, { params: requestData });
    } else {
      response = await instance[method](endpoint, qs.stringify(requestData));
    }

    const normalized = normalizeSuccessResponse(response);
    if (!normalized.ok && normalized.msg) {
      Toast.fail(normalized.msg);
    }
    return normalized;
  } catch (error) {
    const normalized = normalizeErrorResponse(error);
    if (normalized.msg) {
      Toast.fail(normalized.msg);
    }
    return normalized;
  }
}

export { instance };
export default http;
